import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Tooltip,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Fade,
} from "@mui/material";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SummarizeIcon from "@mui/icons-material/Summarize";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import HistoryIcon from "@mui/icons-material/History";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { protectedResources } from "../../authConfig";

const API_BASE = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/sal-committee`;
const BRAND = "#0563B0";
const BRAND_DARK = "#0454a0";

const RUN_TYPE_LABELS = {
  phase_a: "שלב א'",
  phase_b: "שלב ב'",
  competitors: "מתחרים",
};

const RUN_TYPE_COLORS = {
  phase_a: { bg: "#ebf7ff", color: BRAND },
  phase_b: { bg: "#f0faf4", color: "#217346" },
  competitors: { bg: "#fdf2e9", color: "#c2681d" },
};

const triggerBrowserDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Small step header used at the top of each tab panel
const SectionIntro = ({ icon, title, description }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}>
    <Box
      sx={{
        p: 1,
        borderRadius: 2,
        bgcolor: "#ebf7ff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" fontWeight="bold" color="#1f2937">
        {title}
      </Typography>
      <Typography variant="body2" color="#6b7280" sx={{ mt: 0.3 }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

const FileChooserButton = ({ file, placeholder, accept, onChange, icon }) => (
  <Button
    variant="outlined"
    component="label"
    size="small"
    startIcon={icon}
    sx={{
      borderColor: file ? "#217346" : "rgba(0,0,0,0.23)",
      color: file ? "#217346" : "#374151",
      bgcolor: file ? "#f0faf4" : "transparent",
      borderRadius: 2,
      textTransform: "none",
      fontWeight: 600,
      "&:hover": { bgcolor: file ? "#e6f7ec" : "#f8fafc" },
    }}
  >
    {file ? file.name : placeholder}
    <input type="file" hidden accept={accept} onChange={onChange} />
  </Button>
);

const SalCommitteePage = () => {
  const { instance, accounts } = useMsal();

  const [tab, setTab] = useState(0);

  const [phaseAFile, setPhaseAFile] = useState(null);
  const [phaseAState, setPhaseAState] = useState({ loading: false, error: null });
  const [phaseBSourceMode, setPhaseBSourceMode] = useState("latest"); // "latest" | "pick" | "upload"
  const [phaseBSourceFile, setPhaseBSourceFile] = useState(null);
  const [phaseBSourceRunId, setPhaseBSourceRunId] = useState("");
  const [phaseBState, setPhaseBState] = useState({ loading: false, error: null, message: null });
  const [rebuildState, setRebuildState] = useState({ loading: false, error: null, message: null });
  const [rebuildConfirmOpen, setRebuildConfirmOpen] = useState(false);
  const [referenceFile, setReferenceFile] = useState(null);
  const [summaryFile, setSummaryFile] = useState(null);
  const [competitorsState, setCompetitorsState] = useState({ loading: false, error: null });
  const [history, setHistory] = useState({ loading: false, items: [], error: null });

  const getAccessToken = useCallback(async () => {
    const account = accounts[0];
    const request = { scopes: protectedResources.apiScopes.scopes.read, account };
    try {
      const result = await instance.acquireTokenSilent(request);
      return result.accessToken;
    } catch (silentError) {
      // Silent (hidden-iframe) renewal can fail for reasons that aren't about
      // the token itself — third-party cookies blocked, session expired,
      // monitor_window_timeout — same fallback useFetchWithMsal.js already
      // uses elsewhere in this app: fall back to an interactive redirect
      // rather than surfacing a raw MSAL error to the user.
      if (
        silentError instanceof InteractionRequiredAuthError ||
        silentError?.errorCode === "monitor_window_timeout"
      ) {
        await instance.acquireTokenRedirect(request);
        return null; // page is navigating away; caller's request won't complete this cycle
      }
      throw silentError;
    }
  }, [accounts, instance]);

  // Handles a response that may be EITHER a downloadable .xlsx file OR a
  // plain JSON status object (e.g. Phase B when there was nothing new to
  // process) — the backend proxy relays either as-is; branch on the real
  // Content-Type rather than assuming one shape.
  const handleFileOrJsonResponse = async (response, downloadFilename) => {
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try {
        message = JSON.parse(text).message || text;
      } catch {
        // keep raw text
      }
      throw new Error(message || `שגיאה (${response.status})`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("spreadsheetml")) {
      const blob = await response.blob();
      triggerBrowserDownload(blob, downloadFilename);
      return { downloaded: true };
    }
    return response.json();
  };

  const loadHistory = useCallback(async () => {
    setHistory((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`שגיאה בטעינת היסטוריה (${response.status})`);
      const items = await response.json();
      setHistory({ loading: false, items, error: null });
    } catch (err) {
      setHistory({ loading: false, items: [], error: err.message || "שגיאה בטעינת היסטוריה" });
    }
  }, [getAccessToken]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const runPhaseA = async () => {
    if (!phaseAFile) {
      setPhaseAState({ loading: false, error: "יש לבחור קובץ PDF תחילה" });
      return;
    }
    setPhaseAState({ loading: true, error: null });
    try {
      const token = await getAccessToken();
      const formData = new FormData();
      formData.append("pdf_file", phaseAFile);

      const response = await fetch(`${API_BASE}/phase-a/process`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      await handleFileOrJsonResponse(response, "הגשות_לסל.xlsx");
      setPhaseAState({ loading: false, error: null });
      loadHistory();
    } catch (err) {
      setPhaseAState({ loading: false, error: err.message || "שגיאה בהרצת שלב א'" });
    }
  };

  const runPhaseB = async () => {
    if (phaseBSourceMode === "upload" && !phaseBSourceFile) {
      setPhaseBState({ loading: false, error: "יש לבחור קובץ שלב א' להעלאה", message: null });
      return;
    }
    if (phaseBSourceMode === "pick" && !phaseBSourceRunId) {
      setPhaseBState({ loading: false, error: "יש לבחור הרצת שלב א' מההיסטוריה", message: null });
      return;
    }
    setPhaseBState({ loading: true, error: null, message: null });
    try {
      const token = await getAccessToken();
      const formData = new FormData();
      if (phaseBSourceMode === "upload" && phaseBSourceFile) {
        formData.append("phase_a_file", phaseBSourceFile);
      } else if (phaseBSourceMode === "pick" && phaseBSourceRunId) {
        formData.append("phase_a_run_id", phaseBSourceRunId);
      }

      const response = await fetch(`${API_BASE}/phase-b/run`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await handleFileOrJsonResponse(response, "ועדת_סל_סיכום.xlsx");
      const message = result?.downloaded
        ? null
        : result?.message || (result?.status === "no_pending" ? "אין פרוטוקולים חדשים לעיבוד" : null);
      setPhaseBState({ loading: false, error: null, message });
      loadHistory();
    } catch (err) {
      setPhaseBState({ loading: false, error: err.message || "שגיאה בהרצת שלב ב'", message: null });
    }
  };

  const runRebuild = async () => {
    setRebuildConfirmOpen(false);
    setRebuildState({ loading: true, error: null, message: null });
    try {
      const token = await getAccessToken();
      const formData = new FormData();
      if (phaseBSourceMode === "upload" && phaseBSourceFile) {
        formData.append("phase_a_file", phaseBSourceFile);
      } else if (phaseBSourceMode === "pick" && phaseBSourceRunId) {
        formData.append("phase_a_run_id", phaseBSourceRunId);
      }

      const response = await fetch(`${API_BASE}/phase-b/rebuild`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await handleFileOrJsonResponse(response, "ועדת_סל_סיכום.xlsx");
      const message = result?.downloaded
        ? null
        : result?.message || (result?.status === "no_pending" ? "אין פרוטוקולים לעיבוד" : null);
      setRebuildState({ loading: false, error: null, message });
      loadHistory();
    } catch (err) {
      setRebuildState({ loading: false, error: err.message || "שגיאה בבנייה מחדש", message: null });
    }
  };

  const runFindCompetitors = async () => {
    if (!referenceFile || !summaryFile) {
      setCompetitorsState({ loading: false, error: "יש לבחור גם את טבלת הייחוס וגם את קובץ הסיכום" });
      return;
    }
    setCompetitorsState({ loading: true, error: null });
    try {
      const token = await getAccessToken();
      const formData = new FormData();
      formData.append("reference_file", referenceFile);
      formData.append("summary_file", summaryFile);

      const response = await fetch(`${API_BASE}/competitors/find`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      await handleFileOrJsonResponse(response, "מתחרים.xlsx");
      setCompetitorsState({ loading: false, error: null });
      loadHistory();
    } catch (err) {
      setCompetitorsState({ loading: false, error: err.message || "שגיאה באיתור מתחרים" });
    }
  };

  const setAsDefaultRun = async (item) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/history/${item._id}/set-default`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`שגיאה בסימון ברירת מחדל (${response.status})`);
      loadHistory();
    } catch (err) {
      setHistory((prev) => ({ ...prev, error: err.message || "שגיאה בסימון ברירת מחדל" }));
    }
  };

  const downloadHistoryItem = async (item) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/history/${item._id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`שגיאה בהורדה (${response.status})`);
      const blob = await response.blob();
      triggerBrowserDownload(blob, item.filename || "קובץ.xlsx");
    } catch (err) {
      setHistory((prev) => ({ ...prev, error: err.message || "שגיאה בהורדת קובץ" }));
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "";
    try {
      return new Date(isoString).toLocaleString("he-IL");
    } catch {
      return isoString;
    }
  };

  const phaseASourceCount = history.items.filter((item) => item.run_type === "phase_a").length;

  const rebuildDisabled =
    rebuildState.loading ||
    (phaseBSourceMode === "upload" && !phaseBSourceFile) ||
    (phaseBSourceMode === "pick" && !phaseBSourceRunId);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pt: { xs: 10, sm: 11 }, maxWidth: "100%", overflowX: "hidden" }}>
      {/* Header */}
      <Fade in timeout={500}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            mb: 0,
            bgcolor: "#fff",
            border: "1px solid rgba(0,0,0,0.08)",
            borderBottom: "none",
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: "50%",
              bgcolor: "rgba(5,99,176,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LocalPharmacyIcon sx={{ color: BRAND, fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="#1f2937">
              ועדת סל הבריאות
            </Typography>
            <Typography variant="body2" color="#6b7280">
              עיבוד הגשות, עדכון סיכום ואיתור מתחרים
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{ borderRadius: 0, border: "1px solid rgba(0,0,0,0.08)", borderTop: "none", borderBottom: "none" }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              minHeight: 48,
            },
            "& .Mui-selected": { color: BRAND },
            "& .MuiTabs-indicator": { bgcolor: BRAND, height: 3 },
          }}
        >
          <Tab icon={<CloudUploadIcon fontSize="small" />} iconPosition="start" label="שלב א' — עיבוד הגשות" />
          <Tab icon={<SummarizeIcon fontSize="small" />} iconPosition="start" label="שלב ב' — סיכום מצטבר" />
          <Tab icon={<PersonSearchIcon fontSize="small" />} iconPosition="start" label="איתור מתחרים" />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                היסטוריית קבצים
                {history.items.length > 0 && (
                  <Chip
                    label={history.items.length}
                    size="small"
                    sx={{ height: 18, fontSize: "0.68rem", bgcolor: "#ebf7ff", color: BRAND, fontWeight: 700 }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Tab content */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          border: "1px solid rgba(0,0,0,0.08)",
          borderTop: "1px solid rgba(0,0,0,0.06)",
          p: { xs: 2, md: 3 },
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
          minHeight: 320,
        }}
      >
        {/* Tab 0 — Phase A */}
        {tab === 0 && (
          <Box>
            <SectionIntro
              icon={<CloudUploadIcon sx={{ color: BRAND }} />}
              title="שלב א' — עיבוד PDF הגשות"
              description='מעלה PDF של "עיקרי החלטות הוועדה" ומפיק קובץ Excel עם שם החברה לכל תרופה.'
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
              <FileChooserButton
                file={phaseAFile}
                placeholder="בחר קובץ PDF"
                accept="application/pdf"
                icon={<PictureAsPdfIcon fontSize="small" />}
                onChange={(e) => setPhaseAFile(e.target.files?.[0] || null)}
              />
            </Box>

            <Button
              variant="contained"
              onClick={runPhaseA}
              disabled={phaseAState.loading || !phaseAFile}
              startIcon={phaseAState.loading ? null : <CloudUploadIcon />}
              sx={{
                bgcolor: BRAND,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                "&:hover": { bgcolor: BRAND_DARK },
              }}
            >
              {phaseAState.loading ? <CircularProgress size={20} color="inherit" /> : "הרץ שלב א' והורד קובץ"}
            </Button>
            {phaseAState.error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {phaseAState.error}
              </Alert>
            )}
          </Box>
        )}

        {/* Tab 1 — Phase B */}
        {tab === 1 && (
          <Box>
            <SectionIntro
              icon={<SummarizeIcon sx={{ color: "#217346" }} />}
              title="שלב ב' — סיכום מצטבר"
              description="סורק פרוטוקולים חדשים באתר משרד הבריאות ומעדכן את קובץ הסיכום. עלול לקחת מספר דקות."
            />

            <Box sx={{ bgcolor: "#f8fafc", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 2, p: 2, mb: 3 }}>
              <Typography variant="body2" fontWeight={700} color="#1f2937" sx={{ mb: 1.5 }}>
                מקור נתוני שלב א'
              </Typography>
              <RadioGroup
                row
                value={phaseBSourceMode}
                onChange={(e) => setPhaseBSourceMode(e.target.value)}
                sx={{ gap: 1 }}
              >
                <FormControlLabel
                  value="latest"
                  control={<Radio size="small" sx={{ color: BRAND, "&.Mui-checked": { color: BRAND } }} />}
                  label="ברירת מחדל"
                />
                <FormControlLabel
                  value="pick"
                  control={<Radio size="small" sx={{ color: BRAND, "&.Mui-checked": { color: BRAND } }} />}
                  label="בחר מההיסטוריה"
                />
                <FormControlLabel
                  value="upload"
                  control={<Radio size="small" sx={{ color: BRAND, "&.Mui-checked": { color: BRAND } }} />}
                  label="העלה קובץ"
                />
              </RadioGroup>
              {phaseBSourceMode === "latest" && (
                <Typography variant="caption" color="#6b7280" sx={{ display: "block", mt: 0.75 }}>
                  קובץ מסומן בכוכב, אחרת ההרצה האחרונה
                </Typography>
              )}

              {phaseBSourceMode === "pick" && (
                <Box sx={{ mt: 2 }}>
                  {phaseASourceCount === 0 ? (
                    <Typography variant="body2" color="#9ca3af">
                      אין עדיין הרצות שלב א' בהיסטוריה — הרץ שלב א' תחילה
                    </Typography>
                  ) : (
                    <Select
                      size="small"
                      value={phaseBSourceRunId}
                      onChange={(e) => setPhaseBSourceRunId(e.target.value)}
                      displayEmpty
                      sx={{ minWidth: 320, bgcolor: "#fff", borderRadius: 2 }}
                    >
                      <MenuItem value="" disabled>
                        בחר הרצת שלב א'...
                      </MenuItem>
                      {history.items
                        .filter((item) => item.run_type === "phase_a")
                        .map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {formatDate(item.created_at)} — {item.username}
                            {item.is_default ? " ⭐" : ""}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                </Box>
              )}

              {phaseBSourceMode === "upload" && (
                <Box sx={{ mt: 2 }}>
                  <FileChooserButton
                    file={phaseBSourceFile}
                    placeholder="בחר קובץ שלב א' (Excel)"
                    accept=".xlsx,.xls"
                    icon={<DescriptionIcon fontSize="small" />}
                    onChange={(e) => setPhaseBSourceFile(e.target.files?.[0] || null)}
                  />
                </Box>
              )}
            </Box>

            <Button
              variant="contained"
              onClick={runPhaseB}
              disabled={
                phaseBState.loading ||
                (phaseBSourceMode === "upload" && !phaseBSourceFile) ||
                (phaseBSourceMode === "pick" && !phaseBSourceRunId)
              }
              startIcon={phaseBState.loading ? null : <SummarizeIcon />}
              sx={{
                bgcolor: BRAND,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                "&:hover": { bgcolor: BRAND_DARK },
              }}
            >
              {phaseBState.loading ? <CircularProgress size={20} color="inherit" /> : "הרץ שלב ב' והורד קובץ"}
            </Button>
            {phaseBState.message && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                {phaseBState.message}
              </Alert>
            )}
            {phaseBState.error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {phaseBState.error}
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ bgcolor: "#fdf7ed", border: "1px solid #f5e2bd", borderRadius: 2, p: 2 }}>
              <Typography variant="body2" fontWeight={700} color="#92661a" sx={{ mb: 0.5 }}>
                בנייה מחדש מלאה
              </Typography>
              <Typography variant="body2" color="#7a5b28" sx={{ mb: 2 }}>
                בונה מחדש את כל קובץ הסיכום מאפס (עם גיבוי אוטומטי). להשתמש רק לתיקון שורות ישנות —
                הרצה רגילה לא מתקנת שורות קיימות.
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                startIcon={rebuildState.loading ? null : <RestartAltIcon />}
                onClick={() => setRebuildConfirmOpen(true)}
                disabled={rebuildDisabled}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                {rebuildState.loading ? <CircularProgress size={20} color="inherit" /> : "בנה מחדש מאפס"}
              </Button>
              {rebuildState.message && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  {rebuildState.message}
                </Alert>
              )}
              {rebuildState.error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {rebuildState.error}
                </Alert>
              )}
            </Box>

            <Dialog open={rebuildConfirmOpen} onClose={() => setRebuildConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
              <DialogTitle>בנייה מחדש מלאה — לאשר?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  הפעולה תמחק את קובץ הסיכום הנוכחי (עם גיבוי אוטומטי) ותבנה אותו מחדש מכל הפרוטוקולים.
                  זה עלול לקחת כמה דקות. להמשיך?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRebuildConfirmOpen(false)} sx={{ textTransform: "none" }}>
                  ביטול
                </Button>
                <Button onClick={runRebuild} color="warning" variant="contained" sx={{ textTransform: "none", fontWeight: 600 }}>
                  כן, בנה מחדש
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* Tab 2 — Competitors */}
        {tab === 2 && (
          <Box>
            <SectionIntro
              icon={<PersonSearchIcon sx={{ color: "#c2681d" }} />}
              title="איתור מתחרים"
              description="משווה בין קובץ הסיכום לטבלת הייחוס ומוריד קובץ Excel עם המתחרים שנמצאו. להריץ אחרי שלב א' ושלב ב'."
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 0.75 }}>
                  טבלת מילות חיפוש למתחרים
                </Typography>
                <FileChooserButton
                  file={referenceFile}
                  placeholder="בחר קובץ Excel"
                  accept=".xlsx,.xls"
                  icon={<InsertDriveFileIcon fontSize="small" />}
                  onChange={(e) => setReferenceFile(e.target.files?.[0] || null)}
                />
              </Box>

              <Box>
                <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 0.75 }}>
                  קובץ הסיכום המצטבר (שלב א'+ב')
                </Typography>
                <FileChooserButton
                  file={summaryFile}
                  placeholder="בחר קובץ Excel"
                  accept=".xlsx,.xls"
                  icon={<InsertDriveFileIcon fontSize="small" />}
                  onChange={(e) => setSummaryFile(e.target.files?.[0] || null)}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Button
              variant="contained"
              onClick={runFindCompetitors}
              disabled={competitorsState.loading || !referenceFile || !summaryFile}
              startIcon={competitorsState.loading ? null : <PersonSearchIcon />}
              sx={{
                bgcolor: BRAND,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                "&:hover": { bgcolor: BRAND_DARK },
              }}
            >
              {competitorsState.loading ? <CircularProgress size={20} color="inherit" /> : "אתר מתחרים והורד קובץ"}
            </Button>
            {competitorsState.error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {competitorsState.error}
              </Alert>
            )}
          </Box>
        )}

        {/* Tab 3 — History */}
        {tab === 3 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <SectionIntro
                icon={<HistoryIcon sx={{ color: BRAND }} />}
                title="קבצים אחרונים"
                description="כל ההרצות שבוצעו, כולל אפשרות לסמן ברירת מחדל לשלב ב'."
              />
              <Tooltip title="רענן">
                <span>
                  <IconButton onClick={loadHistory} disabled={history.loading} size="small">
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {history.error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {history.error}
              </Alert>
            )}

            {history.loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            ) : history.items.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 5,
                  color: "#9ca3af",
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                  border: "1px dashed rgba(0,0,0,0.12)",
                }}
              >
                <HistoryIcon sx={{ fontSize: 36, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">אין הרצות עדיין</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 700, color: "#374151" }}>סוג</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#374151" }}>תאריך</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#374151" }}>משתמש</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#374151" }}>קובץ</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#374151" }}>
                        ברירת מחדל
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#374151" }}>
                        הורדה
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.items.map((item) => {
                      const colors = RUN_TYPE_COLORS[item.run_type] || { bg: "#f1f5f9", color: "#475569" };
                      return (
                        <TableRow key={item._id} hover>
                          <TableCell>
                            <Chip
                              label={RUN_TYPE_LABELS[item.run_type] || item.run_type}
                              size="small"
                              sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 700, fontSize: "0.72rem" }}
                            />
                          </TableCell>
                          <TableCell>{formatDate(item.created_at)}</TableCell>
                          <TableCell>{item.username}</TableCell>
                          <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.filename}>
                            {item.filename}
                          </TableCell>
                          <TableCell align="center">
                            {item.run_type === "phase_a" && (
                              <Tooltip title={item.is_default ? "ברירת המחדל הנוכחית" : "סמן כברירת מחדל"}>
                                <IconButton size="small" onClick={() => setAsDefaultRun(item)}>
                                  {item.is_default ? (
                                    <StarIcon fontSize="small" sx={{ color: "#f5a623" }} />
                                  ) : (
                                    <StarBorderIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="הורדה">
                              <IconButton size="small" onClick={() => downloadHistoryItem(item)} sx={{ color: BRAND }}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SalCommitteePage;
